import {
  useCallback,
  useEffect,
  useState,
  type ReactNode,
  type KeyboardEvent,
} from 'react'
import { Link } from 'react-router-dom'
import {
  AnimatePresence,
  motion,
  useReducedMotion,
  type PanInfo,
} from 'framer-motion'
import { ArrowUpRight, ChevronLeft, ChevronRight, Hand } from 'lucide-react'
import { Button } from '@/components/ui/Button'
import { LOGO_PATH, LOGO_ALT } from '@/lib/brand'
import { StoreImage } from '@/store/components/StoreImage'
import { BannerTitleText } from '@/store/components/BannerTitleText'
import { preloadImageSrcs } from '@/lib/imageCache'
import { cn } from '@/lib/utils'
import type { Banner } from '@/types'

const AUTO_SLIDE_MS = 5000
const SWIPE_THRESHOLD = 48

const DEFAULT_TITLE = 'Bazm-e-Musk'
const DEFAULT_SUBTITLE =
  'Discover handcrafted attars — rich, long-lasting, and unmistakably luxurious.'

function bannerHref(link?: string) {
  const href = link?.trim()
  return href || null
}

function isExternalHref(href: string) {
  return /^https?:\/\//i.test(href)
}

function BannerTapHint() {
  const reduceMotion = useReducedMotion()

  if (reduceMotion) {
    return (
      <span
        className={cn(
          'pointer-events-none absolute bottom-3 right-3 z-10',
          'inline-flex items-center gap-1.5 rounded-full',
          'border border-gold-400/35 bg-black/60 px-2.5 py-1.5',
          'text-[11px] font-medium uppercase tracking-wide text-gold-300',
          'shadow-[0_4px_16px_rgba(0,0,0,0.35)] backdrop-blur-sm'
        )}
        aria-hidden
      >
        <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
        <span className="hidden sm:inline">Tap to open</span>
      </span>
    )
  }

  return (
    <span
      className="pointer-events-none absolute bottom-3 right-3 z-10"
      aria-hidden
    >
      <span className="relative inline-flex">
        <motion.span
          className="absolute -inset-1 rounded-full border border-gold-400/50"
          animate={{ scale: [1, 1.35], opacity: [0.5, 0] }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeOut',
          }}
        />
        <motion.span
          className={cn(
            'relative inline-flex items-center gap-1.5 rounded-full',
            'border border-gold-400/40 bg-black/65 px-2.5 py-1.5',
            'text-[11px] font-medium uppercase tracking-wide text-gold-300',
            'shadow-[0_4px_16px_rgba(0,0,0,0.35)] backdrop-blur-sm',
            'transition-colors group-hover:border-gold-400/70 group-hover:bg-black/80'
          )}
          animate={{ scale: [1, 1.05, 1] }}
          transition={{
            duration: 1.8,
            repeat: Infinity,
            ease: 'easeInOut',
          }}
        >
          <motion.span
            animate={{ y: [0, -2, 0], rotate: [0, -8, 0] }}
            transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut',
            }}
          >
            <Hand className="h-3.5 w-3.5 shrink-0 text-gold-200" />
          </motion.span>
          <motion.span
            animate={{ x: [0, 2, 0], y: [0, -2, 0] }}
            transition={{
              duration: 1.2,
              repeat: Infinity,
              ease: 'easeInOut',
              delay: 0.15,
            }}
          >
            <ArrowUpRight className="h-3.5 w-3.5 shrink-0" />
          </motion.span>
          <span className="hidden sm:inline">Tap to open</span>
        </motion.span>
      </span>
    </span>
  )
}

const BANNER_RADIUS = 'rounded-2xl'

function BannerLinkWrap({
  href,
  children,
  label,
}: {
  href: string | null
  children: ReactNode
  label: string
}) {
  const className = cn(
    'group relative block h-full w-full overflow-hidden',
    BANNER_RADIUS,
    'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/60',
    href && 'cursor-pointer'
  )

  if (!href) {
    return (
      <div className={cn('relative h-full w-full overflow-hidden', BANNER_RADIUS)}>
        {children}
      </div>
    )
  }

  const sign = <BannerTapHint />

  if (isExternalHref(href)) {
    return (
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        aria-label={label}
        className={className}
      >
        {children}
        {sign}
      </a>
    )
  }

  return (
    <Link to={href} aria-label={label} className={className}>
      {children}
      {sign}
    </Link>
  )
}

function BannerSlideDot({
  active,
  paused,
  reduceMotion,
  slideIndex,
  onSelect,
  label,
}: {
  active: boolean
  paused: boolean
  reduceMotion: boolean
  slideIndex: number
  onSelect: () => void
  label: string
}) {
  const showProgress = active && !reduceMotion && !paused

  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      aria-label={label}
      onClick={onSelect}
      className={cn(
        'relative h-1.5 overflow-hidden rounded-full transition-all duration-300',
        active ? 'w-7 bg-zinc-700' : 'w-1.5 bg-zinc-600 hover:bg-zinc-500'
      )}
    >
      {active && (
        <>
          <span className="absolute inset-0 rounded-full bg-gold-400/20" />
          {showProgress ? (
            <motion.span
              key={slideIndex}
              className="absolute inset-y-0 left-0 rounded-full bg-gold-400"
              initial={{ width: '0%' }}
              animate={{ width: '100%' }}
              transition={{
                duration: AUTO_SLIDE_MS / 1000,
                ease: 'linear',
              }}
            />
          ) : (
            <span className="absolute inset-0 rounded-full bg-gold-400" />
          )}
        </>
      )}
    </button>
  )
}

interface HomeBannerHeroProps {
  banners: Banner[]
}

export function HomeBannerHero({ banners }: HomeBannerHeroProps) {
  const reduceMotion = useReducedMotion() ?? false
  const [activeIndex, setActiveIndex] = useState(0)
  const [paused, setPaused] = useState(false)

  const hasBanners = banners.length > 0
  const hasMultiple = banners.length > 1
  const activeBanner = hasBanners ? banners[activeIndex] : null
  const slideKey = activeBanner?.id ?? 'fallback'

  const goTo = useCallback(
    (index: number) => {
      if (banners.length === 0) return
      const next =
        ((index % banners.length) + banners.length) % banners.length
      setActiveIndex(next)
    },
    [banners.length]
  )

  const goNext = useCallback(() => {
    goTo(activeIndex + 1)
  }, [activeIndex, goTo])

  const goPrev = useCallback(() => {
    goTo(activeIndex - 1)
  }, [activeIndex, goTo])

  useEffect(() => {
    setActiveIndex(0)
  }, [banners])

  useEffect(() => {
    if (!hasMultiple || paused || reduceMotion) return

    const timer = window.setInterval(() => {
      setActiveIndex((current) => (current + 1) % banners.length)
    }, AUTO_SLIDE_MS)

    return () => window.clearInterval(timer)
  }, [banners.length, hasMultiple, paused, reduceMotion])

  useEffect(() => {
    preloadImageSrcs(banners.map((banner) => banner.imageUrl))
  }, [banners])

  const handleSwipeEnd = (_: unknown, info: PanInfo) => {
    if (!hasMultiple) return
    if (info.offset.x <= -SWIPE_THRESHOLD) {
      goNext()
    } else if (info.offset.x >= SWIPE_THRESHOLD) {
      goPrev()
    }
  }

  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!hasMultiple) return
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      goNext()
    } else if (event.key === 'ArrowLeft') {
      event.preventDefault()
      goPrev()
    }
  }

  return (
    <section
      className="relative overflow-hidden arabesque-pattern"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
      onFocusCapture={() => setPaused(true)}
      onBlurCapture={() => setPaused(false)}
      onKeyDown={handleKeyDown}
      aria-roledescription="carousel"
      aria-label="Featured banners"
    >
      <div className="store-container relative py-16 md:py-24 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold-400 mb-4">
            Luxury Attars & Oriental Fragrances
          </p>

          <AnimatePresence mode="wait">
            <motion.div
              key={slideKey}
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: reduceMotion ? 0 : 0.45 }}
            >
              <BannerTitleText
                title={activeBanner?.title || DEFAULT_TITLE}
                className="font-display text-4xl md:text-6xl font-bold leading-tight"
              />
              <p className="mt-2 md:mt-4 text-lg text-zinc-400 max-w-lg">
                {activeBanner?.subtitle || DEFAULT_SUBTITLE}
              </p>
            </motion.div>
          </AnimatePresence>

          <div className="mt-8 flex flex-wrap gap-4">
            <Link to="/shop">
              <Button>Explore Collection</Button>
            </Link>
            <Link to="/shop?featured=1">
              <Button variant="secondary">Featured Attars</Button>
            </Link>
          </div>

          {hasMultiple && (
            <div
              className="mt-6 flex items-center gap-2"
              role="tablist"
              aria-label="Banner slides"
            >
              {banners.map((banner, index) => (
                <BannerSlideDot
                  key={banner.id}
                  active={index === activeIndex}
                  paused={paused}
                  reduceMotion={reduceMotion}
                  slideIndex={activeIndex}
                  onSelect={() => goTo(index)}
                  label={`Show banner ${index + 1}`}
                />
              ))}
              <span className="ml-1 text-[10px] tabular-nums text-zinc-500">
                {activeIndex + 1}/{banners.length}
              </span>
            </div>
          )}
        </div>

        <motion.div
          className={cn(
            'group relative w-full aspect-[21/9]',
            !hasBanners && 'hidden md:block'
          )}
          drag={hasMultiple ? 'x' : false}
          dragConstraints={{ left: 0, right: 0 }}
          dragElastic={0.12}
          onDragEnd={handleSwipeEnd}
        >
          <div
            className={cn(
              'absolute inset-0 overflow-hidden',
              BANNER_RADIUS,
              'glass-card gold-border-glow bg-luxury-800/80'
            )}
          >
            {hasBanners ? (
              banners.map((banner, index) => {
                const href = bannerHref(banner.link)
                const isActive = index === activeIndex
                const label = banner.title
                  ? `Open banner: ${banner.title}`
                  : 'Open banner link'

                return (
                  <motion.div
                    key={banner.id}
                    initial={false}
                    animate={{
                      opacity: isActive ? 1 : 0,
                      scale: isActive ? 1 : 1.02,
                    }}
                    transition={{ duration: reduceMotion ? 0 : 0.45 }}
                    className="absolute inset-0"
                    style={{
                      zIndex: isActive ? 1 : 0,
                      pointerEvents: isActive ? 'auto' : 'none',
                    }}
                    aria-hidden={!isActive}
                  >
                    <BannerLinkWrap href={href} label={label}>
                      <StoreImage
                        src={banner.imageUrl || LOGO_PATH}
                        alt={banner.title || LOGO_ALT}
                        priority={index === 0}
                        objectFit={banner.imageUrl ? 'cover' : 'contain'}
                        spinnerSize="lg"
                        containerClassName={cn(
                          'h-full w-full overflow-hidden bg-luxury-800',
                          BANNER_RADIUS,
                          href &&
                            'transition-transform duration-300 group-hover:scale-[1.02]'
                        )}
                        className={cn(
                          'h-full w-full drop-shadow-[0_0_40px_rgba(212,175,55,0.25)]',
                          BANNER_RADIUS,
                          banner.imageUrl ? 'object-cover' : 'object-contain'
                        )}
                      />
                    </BannerLinkWrap>
                  </motion.div>
                )
              })
            ) : (
              <StoreImage
                src={LOGO_PATH}
                alt={LOGO_ALT}
                priority
                objectFit="contain"
                spinnerSize="lg"
                containerClassName={cn(
                  'h-full w-full overflow-hidden bg-luxury-800',
                  BANNER_RADIUS
                )}
                className={cn(
                  'h-full w-full object-contain drop-shadow-[0_0_40px_rgba(212,175,55,0.25)]',
                  BANNER_RADIUS
                )}
              />
            )}

            <div
              className={cn(
                'pointer-events-none absolute inset-0 bg-gradient-to-t from-black/20 via-transparent to-gold-500/[0.04]',
                BANNER_RADIUS
              )}
              aria-hidden
            />
          </div>

          {hasMultiple && (
            <>
              <button
                type="button"
                onClick={goPrev}
                className={cn(
                  'absolute left-2 top-1/2 z-[3] -translate-y-1/2',
                  'flex h-8 w-8 items-center justify-center rounded-full',
                  'border border-white/10 bg-black/45 text-zinc-200 backdrop-blur-sm',
                  'opacity-0 transition hover:border-gold-400/40 hover:bg-black/65 hover:text-gold-200',
                  'focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/50',
                  'group-hover:opacity-100 max-lg:opacity-80'
                )}
                aria-label="Previous banner"
              >
                <ChevronLeft className="h-4 w-4" />
              </button>
              <button
                type="button"
                onClick={goNext}
                className={cn(
                  'absolute right-2 top-1/2 z-[3] -translate-y-1/2',
                  'flex h-8 w-8 items-center justify-center rounded-full',
                  'border border-white/10 bg-black/45 text-zinc-200 backdrop-blur-sm',
                  'opacity-0 transition hover:border-gold-400/40 hover:bg-black/65 hover:text-gold-200',
                  'focus-visible:opacity-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-gold-400/50',
                  'group-hover:opacity-100 max-lg:opacity-80'
                )}
                aria-label="Next banner"
              >
                <ChevronRight className="h-4 w-4" />
              </button>
            </>
          )}
        </motion.div>
      </div>
    </section>
  )
}
